import type { DocumentId, EditorDocument, ExportWarning, SerializedNodeLike } from "./types";

export const ATTACHMENT_NODE_TYPE = "attachment";
/** The serialized field that holds the reference, as `token` does for a token. */
export const ATTACHMENT_PAYLOAD_KEY = "attachment";

/** The host's name for stored bytes. Opaque to the editor. */
export type AssetId = string;

/**
 * What a document keeps of an image or a file: which asset it is, and enough to say what it was
 * when the asset cannot be reached. Never the bytes, a URL, or anything that expires; where the
 * bytes are, and whether they have been uploaded, is the host's asset store's to know.
 */
export interface AttachmentRef {
  assetId: AssetId;
  /** `type/subtype`, lower case, no parameters. An image is whatever starts with `image/`. */
  mimeType: string;
  /** The file's name, shown on a file's card and wherever the asset cannot be shown. */
  name: string;
  /** Bytes. A label, not a bound: the limit is applied when a file comes in. */
  size: number;
  /** What an image shows, for someone who cannot see it. Empty until somebody writes it. */
  alt: string;
  /** Pixels, for an image whose size is known: the space is kept before the image arrives. */
  width?: number;
  height?: number;
}

/** Something the gate changed about an attachment so that the content fits. */
export type AttachmentNote =
  /**
   * An attachment became its name: this editor has no attachments, or it was pasted where only
   * text can stand.
   */
  | "attachment-flattened"
  /**
   * Pasted markup held images, and they were left out: all it has of them is an address somewhere
   * else, and a document only refers to what the host's store keeps.
   */
  | "images-dropped";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * URL-safe and without a colon or a slash, so that a blob URL, a data URL, or a signed link cannot
 * be stored as an ID even by mistake.
 */
const ASSET_ID_PATTERN = /^[A-Za-z0-9._~-]{1,128}$/;
const MIME_TYPE_PATTERN = /^[a-z0-9][a-z0-9!#$&^_.+-]{0,126}\/[a-z0-9][a-z0-9!#$&^_.+-]{0,126}$/;
const MAX_NAME_LENGTH = 255;
const MAX_ALT_LENGTH = 2000;
const REF_KEYS: ReadonlySet<string> = new Set([
  "assetId",
  "mimeType",
  "name",
  "size",
  "alt",
  "width",
  "height",
]);

export const isAssetId = (value: unknown): value is AssetId =>
  typeof value === "string" && ASSET_ID_PATTERN.test(value);

const isPixels = (value: unknown): boolean =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

/**
 * Returns what is wrong with a reference, or `null`. One check for every place a reference comes
 * from: a stored document, the clipboard, the host's adapter, and the composer's tray.
 */
export function checkAttachmentRef(value: unknown): string | null {
  if (!isRecord(value)) return "attachment is not an object";
  if (!isAssetId(value.assetId)) return "assetId is not an asset ID";
  if (typeof value.mimeType !== "string" || !MIME_TYPE_PATTERN.test(value.mimeType)) {
    return "mimeType is not a media type";
  }
  if (typeof value.name !== "string" || value.name === "" || value.name.length > MAX_NAME_LENGTH) {
    return "name is not a file name";
  }
  if (typeof value.size !== "number" || !Number.isSafeInteger(value.size) || value.size < 0) {
    return "size is not a byte count";
  }
  if (typeof value.alt !== "string" || value.alt.length > MAX_ALT_LENGTH) {
    return "alt is not text";
  }
  // Both or neither: one of them alone says nothing about the space to keep.
  if ((value.width === undefined) !== (value.height === undefined)) {
    return "width and height are not given together";
  }
  if (value.width !== undefined && !(isPixels(value.width) && isPixels(value.height))) {
    return "width or height is not a pixel count";
  }
  // Nothing else is stored with a reference, so nothing that expires or grants access can be.
  for (const key of Object.keys(value)) {
    if (!REF_KEYS.has(key)) return `${key} is not part of an attachment`;
  }
  return null;
}

/** The reference a serialized node holds, or `undefined` when it holds none that is well formed. */
export function readAttachment(node: SerializedNodeLike): AttachmentRef | undefined {
  if (node.type !== ATTACHMENT_NODE_TYPE) return undefined;
  const payload = node[ATTACHMENT_PAYLOAD_KEY];
  return checkAttachmentRef(payload) === null ? (payload as AttachmentRef) : undefined;
}

export const isImageAttachment = (attachment: AttachmentRef): boolean =>
  attachment.mimeType.startsWith("image/");

/** What an attachment is called where it cannot be shown: plain text, Markdown, a flattened paste. */
export const attachmentLabel = (attachment: AttachmentRef): string =>
  attachment.alt === "" ? attachment.name : attachment.alt;

const UNITS = ["KB", "MB", "GB", "TB"];

/** `1.4 MB`. Decimal units, as file managers show them. */
export function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  let value = bytes;
  let unit = -1;
  do {
    value /= 1000;
    unit++;
  } while (value >= 1000 && unit < UNITS.length - 1);
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${UNITS[unit]}`;
}

/**
 * Whether content holds an attachment. Only the top level is looked at, because that is the only
 * place a stored attachment can be; the check runs on every document that is opened.
 */
export function usesAttachments(root: unknown): boolean {
  if (!isRecord(root) || !Array.isArray(root.children)) return false;
  return root.children.some((child) => isRecord(child) && child.type === ATTACHMENT_NODE_TYPE);
}

/**
 * Every asset a document refers to, once each, in document order. The editor never deletes an
 * asset: a host decides what is unreferenced by reading this from the documents it keeps.
 */
export function listAssetIds(document: EditorDocument): AssetId[] {
  const found = new Set<AssetId>();
  const stack: unknown[] = [document.content.root];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!isRecord(node)) continue;
    const attachment = readAttachment(node as SerializedNodeLike);
    if (attachment !== undefined) found.add(attachment.assetId);
    if (Array.isArray(node.children)) {
      for (let index = node.children.length - 1; index >= 0; index--) {
        stack.push(node.children[index]);
      }
    }
  }
  return [...found];
}

/**
 * An attachment in Markdown: a label in brackets, which Markdown reads as the text it is. The
 * document holds no address for the asset, so there is no link to write.
 */
export const attachmentMarkdown = (attachment: AttachmentRef): string =>
  `[${isImageAttachment(attachment) ? "image" : "file"}: ${attachmentLabel(attachment)}]`;

/** The warning an export reports for an attachment: its label is in the output, the asset is not. */
export const attachmentLostWarning = (
  attachment: AttachmentRef,
  blockId?: string,
): ExportWarning => ({
  code: "attachment-not-exported",
  message: `"${attachment.name}" was exported as a label; the file is not in the output.`,
  ...(blockId === undefined ? {} : { blockId }),
});

/** The line of text an attachment becomes where it cannot stand as itself. */
export const attachmentAsText = (node: SerializedNodeLike): SerializedNodeLike | null => {
  const attachment = readAttachment(node);
  return attachment === undefined
    ? null
    : { type: "text", version: 1, text: attachmentLabel(attachment) };
};

/** Where the bytes of an asset stand with the host. Never part of a document or its history. */
export type AttachmentStatus =
  /** Kept durably on this device; not sent anywhere yet. */
  | { state: "staged" }
  /** `progress` runs from 0 to 1, or is `null` where the transport cannot say. */
  | { state: "uploading"; progress: number | null }
  | { state: "uploaded" }
  | { state: "failed"; retryable: boolean; message?: string }
  /** The host has no such asset, or cannot reach it. */
  | { state: "missing" };

export type StageRefusal = "too-large" | "unsupported-type" | "quota" | "failed";

export type StageResult =
  | { status: "staged"; attachment: AttachmentRef }
  | { status: "refused"; reason: StageRefusal; message?: string };

export interface StageContext {
  /** The document the file was given to. */
  documentId: DocumentId;
  /** Aborted when the editor that took the file is gone. */
  signal: AbortSignal;
}

/** Something to show for an asset. `release` is called when it is no longer shown. */
export interface ResolvedAttachment {
  /** An object URL or the host's own. Used for display only; it is never stored. */
  url: string;
  release(): void;
}

/**
 * The host's asset store, as the editor sees it. The editor asks it to keep a file and is given a
 * reference to put in the document; from then on the bytes, the upload, and when an asset may be
 * deleted are the host's business. The editor never asks for an asset to be deleted: undo can
 * bring back a block long after it was removed (see `listAssetIds`).
 */
export interface AttachmentAdapter {
  /**
   * Keeps a file durably on this device and names it. Resolves once that is done, and not when
   * an upload is: a document only ever refers to an asset that survives a reload.
   */
  stage(file: File, context: StageContext): Promise<StageResult>;
  /** Something to show, or `null` when the asset cannot be reached. */
  resolve(assetId: AssetId, context: { signal: AbortSignal }): Promise<ResolvedAttachment | null>;
  /**
   * Calls the listener with the asset's status as soon as it is known, and again whenever it
   * changes. Returns what stops it.
   */
  watch(assetId: AssetId, listener: (status: AttachmentStatus) => void): () => void;
  retry(assetId: AssetId): void;
  cancel(assetId: AssetId): void;
  /**
   * Where a composer's tray is kept between visits: the attachments given to a message that has
   * not been sent. Without it the tray lasts as long as the page.
   */
  pending?: {
    list(documentId: DocumentId): Promise<readonly AttachmentRef[]>;
    /** The whole tray, every time: writing the same list twice changes nothing. */
    set(documentId: DocumentId, attachments: readonly AttachmentRef[]): Promise<void>;
  };
}
